'use client';
import apiRequest from '@lib/api-fetch';
import usioApi from '@lib/api-fetch/usio-api';
import { EmailProvider } from 'types/send-email';
import { FiservCheckoutReq } from 'types/checkout';
import { UsioCardholder } from 'types/usioProfile';

export async function getGiftCardsByOrder(order_id: string): Promise<any> {
    const response = await apiRequest(
        'GET',
        `/order/gift-cards/${order_id}`,
        {},
        '',
        true
    );
    return response.body;
}

export async function getGiftCardByProvisionedCardId(
    provisioned_card_id: string
): Promise<any> {
    let response: any;
    console.log('apiRequest getGiftCardByProvisionedCardId');
    try {
        response = await apiRequest(
            'GET',
            `/order/gift-card/${provisioned_card_id}`
        );
        console.log(response);
    } catch (error) {
        console.error(error);
        throw error;
    }

    return response.body;
}

export async function getRedemptionTransactionDetails(
    id: string
): Promise<any> {
    const auth_token = sessionStorage.getItem(`auth_token_${id}`) || '';
    const response = await apiRequest(
        'GET',
        `/redemption/${id}`,
        {},
        auth_token
    );
    return response.body;
}

export async function getDeliveryInfo(id: string): Promise<any> {
    const response = await apiRequest(
        'GET',
        `/redemption/delivery-info/${id}`,
        {}
    );
    return response.body;
}

export async function getRedemptionCardInfo(id: string): Promise<any> {
    const auth_token = sessionStorage.getItem(`auth_token_${id}`) || '';
    const response = await apiRequest(
        'GET',
        `/redemption/widget/${id}`,
        {},
        auth_token
    );
    return response.body;
}

export async function redemptionVerify(params: {
    provisioned_card_id?: string;
    email_provider?: EmailProvider;
}): Promise<any> {
    const response = await apiRequest('POST', `/otp/initiate`, {
        body: {
            ...params,
            email_provider: params.email_provider ?? EmailProvider.SendGrid,
        },
    });
    return response;
}

export async function getSmsStatus(sid: string): Promise<any> {
    const response = await apiRequest('GET', `/otp/getstatus/${sid}`);
    return response;
}

export async function getEmailStatus(
    sid: string,
    emailProvider: EmailProvider = EmailProvider.SendGrid
): Promise<any> {
    const response = await apiRequest(
        'GET',
        `/otp/getemailstatus/${sid}?email_provider=${emailProvider}`
    );
    return response;
}

export async function redemptionConfirmVerify(params: {
    provisioned_card_id: string;
    otp: string;
}): Promise<any> {
    const response = await apiRequest('POST', `/otp/validate`, {
        body: params,
    });
    return response;
}

export async function smsRedemption(provisioned_card_id: string): Promise<any> {
    const response = await apiRequest(
        'POST',
        `/redemption/sms-redemption/${provisioned_card_id}`,
        { body: {} }
    );
    return response;
}

export async function sendButtonTapped(
    provisioned_card_id: string
): Promise<any> {
    const response = await apiRequest(
        'PUT',
        `/redemption/send-button-tapped/${provisioned_card_id}`,
        { body: {} }
    );
    return response;
}

export async function giftCardChangeBusiness(params: {
    gift_card_id: string;
    search_provider: string;
    business_id: string;
    business_name: string;
    photo_img_url: string;
    rating: number;
    review_count: number;
    provisioned_card_id?: string | null;
}): Promise<any> {
    const response = await apiRequest(
        'PUT',
        `/order/gift-card/change-business`,
        {
            body: params,
        }
    );
    return response;
}

export async function trackingSendPrepaidGiftCard(
    provisioned_card_id?: string | null
): Promise<any> {
    try {
        const response = await apiRequest(
            'PUT',
            `/order/gift-card/tracking-send-prepaid-gift-card/${provisioned_card_id}`
        );
        return response;
    } catch {
        console.log('trackingSendPrepaidGiftCard - error');
    }
}

export async function trackingForMarketing(order_id: string): Promise<any> {
    const response = await apiRequest(
        'PUT',
        `/customer/promotional-emails/${order_id}`
    );
    return response;
}

export async function fiservAuthorizeSession(): Promise<any> {
    const response = await apiRequest('POST', `/fiserv/authorize-session`, {
        body: {},
    });
    return response;
}

export async function fiservCheckout(request: FiservCheckoutReq): Promise<any> {
    const response = await apiRequest('POST', `/fiserv/checkout`, {
        body: request,
    });
    return response;
}

export async function usioGetCardNumber(
    auth_token: string,
    username: string
): Promise<any> {
    return await usioApi('GET', `/card/number`, {}, auth_token, username);
}

export async function usioGetCardCvv(
    auth_token: string,
    username: string
): Promise<any> {
    return await usioApi('GET', `/card/cvv`, {}, auth_token, username);
}

export const getCardholderProfile = (auth_token: string) => {
    return usioApi('GET', `/cardholder/profile`, {}, auth_token);
};

export const usioUpdatePhoneNumber = (
    req: UsioCardholder,
    auth_token: string
): Promise<any> => {
    return usioApi('PUT', `/cardholder/profile`, { body: req }, auth_token);
};

export async function usioGetCardBalance(
    auth_token: string,
    username: string
): Promise<any> {
    return await usioApi('GET', `/card/balance`, {}, auth_token, username);
}

export async function validateBuyer(request: {
    cart_id: string;
    email: string;
    phone: string;
}): Promise<any> {
    const response = await apiRequest('POST', `/checkout/validate-buyer`, {
        body: request,
    });
    return response;
}

export async function validateLogin(request: {
    email: string;
    password: string;
}): Promise<any> {
    const response = await apiRequest('POST', `/custom-auth/validate-login`, {
        body: request,
    });
    return response;
}

export async function generate2FA(request: {
    customer_id: string;
    email_provider?: EmailProvider;
}): Promise<any> {
    const response = await apiRequest('POST', `/custom-auth/generate-2fa`, {
        body: request,
    });
    return response;
}

export async function verify2FA(request: {
    customer_id: string;
    code: string;
}): Promise<any> {
    const response = await apiRequest('POST', `/custom-auth/verify-2fa`, {
        body: request,
    });
    return response;
}

export async function getPaymentSessionStatus(cart_id: string): Promise<any> {
    const response = await apiRequest(
        'GET',
        `/payment-session/status/${cart_id}`
    );
    return response;
}

export async function verifyRecaptchaToken(
    token: string,
    action: string
): Promise<any> {
    const response = await apiRequest(
        'POST',
        `/recaptcha/verify-checkout-token`,
        {
            body: {
                event: {
                    token: token,
                    expectedAction: action,
                    siteKey: process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_KEY,
                },
            },
        }
    );

    return response;
}

export async function checkAfterHoursProcessing(
    provisioned_card_id: string
): Promise<boolean> {
    const timezoneOffset = new Date().getTimezoneOffset();
    const response = await apiRequest('GET', `/redemption/check-after-hours-processing/${provisioned_card_id}?timezoneOffset=${timezoneOffset}`, {});
    return response.body;
}